// src/lib/intake/resultsModel.js
//
// Ticket 069 (Advisor Intake I7). Pure: maps the masked results payload that
// intake-calculate returns (plus what the page already holds) to what the Results
// page draws. No React, no network, no policy: the server already decided which
// blocks exist (clientVisibility.js on the server), and this only reads them.
//
// "Not assessed" lines (decision D2): a fact the intake asks that is still open does
// NOT block the results; it is listed so the page can say what was left out. The
// list is built from the payload's `gaps` and `completeness.open_facts`, filtered
// by INTAKE_ASKED_FACTS (facts the intake never asks, or only derives, are never
// shown), grouped by the screen that fixes them.

import { SECTION1_SCREEN_ORDER, REVIEW_ROW_LABELS, RESULTS_NOT_ASSESSED_IMPACT, RESULTS_NOT_ASSESSED_GENERIC,
  RESULTS_NO_INCOME_REASON, RESULTS_ENGINE_BLOCKED_REASON } from './intakeConfig'
import { screensAsking } from './askedFacts'
import { shouldShowScreen } from './section23Facts'
import { getEligibilityBlock } from '../../hooks/useCalculatorForm'

// Eligibility code (server) -> the Calculator's field key and the answer that trips it.
// The Calculator's own reason text is reused so the wording cannot drift.
const ELIGIBILITY_CODES = {
  foral: (comunidad) => ({ field: 'comunidad', answers: { comunidad: comunidad || 'pais_vasco' } }),
  non_resident: () => ({ field: 'residency', answers: { residency_year_context: 'non_resident' } }),
  beckham_active: () => ({ field: 'beckham_status', answers: { beckham_status: 'active' } }),
}

export function blockedReasons(codes, comunidad) {
  const out = []
  for (const code of codes || []) {
    const make = ELIGIBILITY_CODES[code]
    if (!make) continue
    const { field, answers } = make(comunidad)
    const block = getEligibilityBlock(field, answers)
    if (block && block.reason && !out.includes(block.reason)) out.push(block.reason)
  }
  return out
}

const isShown = (id, ctx) => SECTION1_SCREEN_ORDER.includes(id) || shouldShowScreen(id, ctx)

// The screen that fixes `key`, or null when the intake never asks it.
function fixScreenFor(key, ctx) {
  const screens = screensAsking(key)
  if (screens.length === 0) return null
  return screens.find((id) => isShown(id, ctx)) || null
}

export function notAssessed(results, ctx) {
  const open = (results && results.completeness && results.completeness.open_facts) || []
  const gaps = (results && results.gaps) || []
  const seen = new Set()
  const lines = []
  const add = (domain, fact, status) => {
    const key = `${domain}.${fact}`
    // A skipped income is the reduced-findings note, not a "not assessed" line.
    if (status === 'unknown' && (key === 'income.self_income_items' || key === 'income.spouse_income_items')) return
    const screen = fixScreenFor(key, ctx)
    if (!screen || seen.has(screen)) return
    seen.add(screen)
    lines.push({
      screen,
      label: REVIEW_ROW_LABELS[screen] || screen,
      impact: RESULTS_NOT_ASSESSED_IMPACT[screen] || RESULTS_NOT_ASSESSED_GENERIC,
    })
  }
  for (const f of open) add(f.domain, f.fact, f.status)
  for (const g of gaps) add(g.domain, g.fact, g.status)
  return lines
}

export function incomeSkipped(results) {
  const open = (results && results.completeness && results.completeness.open_facts) || []
  return open.some((f) => f.domain === 'income' && f.fact === 'self_income_items' && f.status === 'unknown')
}

// input: { results, ctx ({answers, suggestions, dismissed}), comunidad, savedRevision, saving }
export function buildResultsView(input) {
  const { results, ctx, comunidad = null, savedRevision = null, saving = false } = input
  const meta = (results && results.meta) || {}
  const lines = notAssessed(results, ctx)

  let estimate
  if (results.irpf) {
    estimate = { kind: 'estimate' }
  } else if (results.irpf_unavailable) {
    const { reason, codes } = results.irpf_unavailable
    let reasons = []
    if (reason === 'eligibility_blocked') reasons = blockedReasons(codes, comunidad)
    else if (reason === 'no_income_with_amount') reasons = [RESULTS_NO_INCOME_REASON]
    else reasons = [RESULTS_ENGINE_BLOCKED_REASON]
    estimate = { kind: 'blocked', reasons }
  } else if (results.notice) {
    estimate = { kind: 'hidden', notice: results.notice }
  } else {
    estimate = { kind: 'none' }
  }

  const saved = savedRevision != null && savedRevision === meta.facts_revision
  return {
    rateYear: meta.irpf_rate_year,
    factsRevision: meta.facts_revision,
    estimate,
    hasAutonomo: !!results.autonomo,
    notAssessed: lines,
    incomeSkipped: incomeSkipped(results),
    saved,
    canSave: !saved && !saving && typeof meta.facts_revision === 'number',
  }
}
